import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle, Phone, MessageCircle, MapPin, Clock, Search, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-5 px-6 text-left hover:bg-slate-50 transition-colors duration-200"
        data-testid={`faq-question-${question.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-teal-600 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-5 animate-fade-in">
          <div className="text-slate-600 leading-relaxed">{answer}</div>
        </div>
      )}
    </div>
  );
};

// Medical Aid Checker Component
const MedicalAidChecker = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [result, setResult] = useState(null);

  // List of accepted medical aids
  const acceptedMedicalAids = [
    'discovery', 'gems', 'bonitas', 'momentum', 'medshield', 'polmed', 
    'medihelp', 'bestmed', 'camaf', 'universal', 'medscheme', 'samwumed',
    'fedhealth', 'profmed', 'bankmed', 'hosmed', 'keyhealth', 'sizwe',
    'compcare', 'resolution', 'spectramed', 'umvuzo', 'liberty', 'anglo medical',
    'sasolmed', 'transmed', 'mbmed', 'thebemed', 'makoti', 'selfmed',
    'netcare', 'massmart', 'woolworths', 'pick n pay', 'shoprite', 'sanlam'
  ];

  // Plans that are not accepted
  const notAcceptedPlans = [
    'discovery keycare', 'gems emerald', 'bonitas boncap'
  ];

  const checkMedicalAid = (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setResult(null);
      return;
    }
    
    const lowerTerm = term.toLowerCase();
    
    // Check if it's a non-accepted plan
    const isNotAccepted = notAcceptedPlans.some(plan => lowerTerm.includes(plan));
    if (isNotAccepted) {
      setResult({ accepted: false, message: 'This specific plan may not be covered. Please contact us for confirmation.' });
      return;
    }
    
    // Check if it matches accepted medical aids
    const isAccepted = acceptedMedicalAids.some(aid => lowerTerm.includes(aid) || aid.includes(lowerTerm));
    if (isAccepted) {
      setResult({ accepted: true, message: 'Great news! We are contracted with this medical aid provider.' });
    } else if (term.length >= 3) {
      setResult({ accepted: null, message: 'We couldn\'t find this in our database. Please contact us for verification.' });
    }
  };

  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-xl">
      <p className="text-sm text-slate-600 mb-3">Verify your medical aid coverage:</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Enter your medical aid name..."
          value={searchTerm}
          onChange={(e) => checkMedicalAid(e.target.value)}
          className="pl-10 rounded-lg"
          data-testid="medical-aid-search"
        />
      </div>
      {result && (
        <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
          result.accepted === true ? 'bg-green-50 text-green-700' : 
          result.accepted === false ? 'bg-red-50 text-red-700' : 
          'bg-amber-50 text-amber-700'
        }`}>
          {result.accepted === true ? <CheckCircle className="w-5 h-5" /> : 
           result.accepted === false ? <XCircle className="w-5 h-5" /> : 
           <HelpCircle className="w-5 h-5" />}
          <span className="text-sm font-medium">{result.message}</span>
        </div>
      )}
    </div>
  );
};

export const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <SEO 
        title="Frequently Asked Questions"
        description="Find answers to common questions about K&K Medical and Dental. Learn about our services, pricing, medical aids accepted, operating hours, and more."
        keywords="FAQ doctor Umzinto, medical clinic questions, dental FAQ, medical aid questions, GP consultation FAQ"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6 animate-fade-in-up">
            Got Questions?
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6 animate-fade-in-up animation-delay-100">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            Find answers to common questions about our services, pricing, and policies. 
            Can't find what you're looking for? Contact us directly.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="faq-content-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* General */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-600" />
              General
            </h2>
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <FAQItem 
                  question="Do I need an appointment for a consultation?" 
                  answer={
                    <p>We operate on a convenient walk-in basis for general medical consultations, ensuring you receive care when you need it most. However, to optimise your experience and minimise waiting times, we recommend scheduling appointments for dental services. Surgical procedures require advance booking to ensure proper preparation and dedicated care.</p>
                  }
                  isOpen={openItems['g1']} 
                  onClick={() => toggleItem('g1')} 
                />
                <FAQItem 
                  question="What are your operating hours?" 
                  answer={
                    <div>
                      <p className="mb-3">Our practice maintains extended hours to accommodate the diverse schedules of our patients:</p>
                      <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                        <Clock className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-slate-700">Monday – Friday: 8:00 AM – 5:00 PM</p>
                          <p className="font-medium text-slate-700">Saturday: 8:00 AM – 2:00 PM</p>
                          <p className="font-medium text-slate-700">Sunday & Public Holidays: Closed</p>
                        </div>
                      </div>
                    </div>
                  }
                  isOpen={openItems['g2']} 
                  onClick={() => toggleItem('g2')} 
                />
                <FAQItem 
                  question="Where is K&K Medical and Dental located?" 
                  answer={
                    <div>
                      <p className="mb-3">We have two convenient locations in KwaZulu-Natal:</p>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                          <MapPin className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                          <div className="notranslate">
                            <p className="font-semibold text-teal-700 mb-1">Umzinto (Main Branch)</p>
                            <p className="font-medium text-slate-700">Lot 293 Nelson Mandela Road</p>
                            <p className="font-medium text-slate-700">Umzinto, KwaZulu-Natal</p>
                            <p className="text-slate-600 text-sm mt-1">Services: Medical, Surgical, Insurance & Medicals, IV Infusions (Limited)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
                          <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                          <div className="notranslate">
                            <p className="font-semibold text-blue-700 mb-1">Scottburgh (New Branch)</p>
                            <p className="font-medium text-slate-700">Scott Health Medical Center</p>
                            <p className="font-medium text-slate-700">41 Airth Street, Scottburgh, KZN</p>
                            <p className="text-slate-600 text-sm mt-1">Services: All services including Dental & Specialist Services</p>
                          </div>
                        </div>
                      </div>
                      
                      <a 
                        href="https://www.google.com/maps/dir/?api=1&destination=-30.3096049,30.6633522&destination_place_id=ChIJMUVpO3Q59h4R2uVVVUP1kmA" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                      >
                        <MapPin className="w-4 h-4" />
                        Get directions to Umzinto →
                      </a>
                    </div>
                  }
                  isOpen={openItems['g3']} 
                  onClick={() => toggleItem('g3')} 
                />
                <FAQItem 
                  question="How long is the average waiting time?" 
                  answer={
                    <p>We pride ourselves on efficient patient flow management. Under normal circumstances, you can expect to be seen within approximately 15 minutes of arrival. During peak periods, waiting times may extend up to one hour. We appreciate your patience and assure you that every patient receives our full attention and comprehensive care.</p>
                  }
                  isOpen={openItems['g4']} 
                  onClick={() => toggleItem('g4')} 
                />
                <FAQItem 
                  question="Do you see patients of all ages?" 
                  answer={
                    <p>Absolutely. Our practice is committed to providing comprehensive healthcare across all life stages. From paediatric care for infants and children to geriatric services for our senior patients, our experienced medical team is equipped to address the unique health needs of every family member under one roof.</p>
                  }
                  isOpen={openItems['g5']} 
                  onClick={() => toggleItem('g5')} 
                />
              </CardContent>
            </Card>
          </div>

          {/* Payment & Medical Aid */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-600" />
              Payment & Medical Aid
            </h2>
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <FAQItem 
                  question="Which medical aids do you accept?" 
                  answer={
                    <div>
                      <p>We are proud to be contracted with all major medical aid schemes in South Africa, including GEMS, Discovery, Bonitas, Polmed, Medshield, Boncap, Discovery KeyCare, Bestmed, Universal, CAMAF, and many more. While we accommodate most plans, certain restricted options may have limited coverage. Please contact our administrative team for personalised assistance.</p>
                    </div>
                  }
                  isOpen={openItems['p1']} 
                  onClick={() => toggleItem('p1')} 
                />
                <FAQItem 
                  question="What payment methods do you accept?" 
                  answer={
                    <p>For your convenience, we accept all major South African debit and credit cards, including Visa and Mastercard, as well as cash payments. Please note that international cards such as American Express and Diners Club are not currently supported at our facility. Our administrative staff will be happy to assist with any payment-related enquiries.</p>
                  }
                  isOpen={openItems['p2']} 
                  onClick={() => toggleItem('p2')} 
                />
                <FAQItem 
                  question="How much does a consultation cost?" 
                  answer={
                    <p>We offer <span className="font-semibold text-teal-600">personalised pricing</span> based on your specific needs. Please WhatsApp us on <a href="https://wa.me/27828951916" className="text-teal-600 hover:text-teal-700 underline font-medium">082 895 1916</a> or call during operating hours for a current quote. We maintain transparent pricing and believe quality healthcare should never be a financial burden.</p>
                  }
                  isOpen={openItems['p3']} 
                  onClick={() => toggleItem('p3')} 
                />
                <FAQItem 
                  question="Do you offer payment plans?" 
                  answer={
                    <p>After careful consideration of our community's needs, we made a strategic decision to restructure our pricing model rather than implement payment plans. Recognising the economic challenges facing many families in our region, we have significantly reduced our fees to ensure immediate accessibility. Our goal is to eliminate financial barriers to healthcare, making quality medical services attainable for all without the complexity of deferred payment arrangements.</p>
                  }
                  isOpen={openItems['p4']} 
                  onClick={() => toggleItem('p4')} 
                />
                <FAQItem 
                  question="Can I claim from my medical aid for services?" 
                  answer={
                    <p>Certainly. For patients who prefer to pay out-of-pocket, we provide comprehensive documentation including a detailed invoice of services rendered and an official receipt. These documents are fully compliant with medical aid requirements and can be submitted directly to your scheme for reimbursement. Our administrative team can guide you through the claims process if needed.</p>
                  }
                  isOpen={openItems['p5']} 
                  onClick={() => toggleItem('p5')} 
                />
              </CardContent>
            </Card>
          </div>

          {/* Medical Services */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-600" />
              Medical Services
            </h2>
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <FAQItem 
                  question="What medical services do you offer?" 
                  answer={
                    <p>Our practice offers an extensive range of medical services designed to meet the diverse healthcare needs of our community. From routine consultations and preventive care to specialised treatments and diagnostic services, we provide comprehensive healthcare solutions under one roof. <Link to="/services" className="text-teal-600 hover:text-teal-700 font-medium">Explore our complete service offering →</Link></p>
                  }
                  isOpen={openItems['m1']} 
                  onClick={() => toggleItem('m1')} 
                />
                <FAQItem 
                  question="Do you provide chronic medication management?" 
                  answer={
                    <p>We are deeply committed to the ongoing wellbeing of patients with chronic conditions. Our chronic care programme encompasses regular monitoring, medication optimisation, and lifestyle guidance to ensure optimal disease management. Additionally, we serve as an authorised collection point for Medilogistics, our partner pharmacy service provider for medical aid chronic medication. This convenient service ensures uninterrupted access to your essential medications. <Link to="/contact" className="text-teal-600 hover:text-teal-700 font-medium">Contact us</Link> to learn more about enrolling in our chronic care programme.</p>
                  }
                  isOpen={openItems['m2']} 
                  onClick={() => toggleItem('m2')} 
                />
                <FAQItem 
                  question="Can I get a sick note for work or school?" 
                  answer={
                    <p>Medical certificates are issued following a thorough clinical assessment by our treating physician. In accordance with professional medical ethics and legal requirements, sick notes are provided only when the doctor determines, based on clinical findings, that the patient is medically unfit to perform their work duties or attend educational activities. This ensures the integrity of medical documentation while protecting both patients and employers.</p>
                  }
                  isOpen={openItems['m3']} 
                  onClick={() => toggleItem('m3')} 
                />
                <FAQItem 
                  question="Do you offer pregnancy ultrasounds?" 
                  answer={
                    <p>Yes, we provide professional obstetric ultrasound services as part of our comprehensive women's health programme. Our prenatal imaging includes detailed scans with printed images for expectant parents to treasure. These examinations play a crucial role in monitoring foetal development and ensuring a healthy pregnancy journey. <Link to="/services#medical" className="text-teal-600 hover:text-teal-700 font-medium">View our medical services →</Link></p>
                  }
                  isOpen={openItems['m4']} 
                  onClick={() => toggleItem('m4')} 
                />
                <FAQItem 
                  question="What vaccinations do you provide?" 
                  answer={
                    <p>Our vaccination services encompass the full spectrum of immunisations recommended for various age groups and health conditions. We are particularly proud to offer Yellow Fever vaccinations at our Scottburgh location, which holds official accreditation from the Department of Health as a certified Yellow Fever Vaccination Centre. Whether you require routine immunisations, travel vaccines, or occupational health vaccinations, our qualified healthcare professionals ensure safe and proper administration.</p>
                  }
                  isOpen={openItems['m5']} 
                  onClick={() => toggleItem('m5')} 
                />
                <FAQItem 
                  question="Do you perform minor surgical procedures?" 
                  answer={
                    <p>Our practice is fully equipped to perform a comprehensive range of minor surgical procedures. Utilising modern techniques and appropriate anaesthesia, we ensure that all procedures are conducted with minimal discomfort. Our experienced medical team prioritises patient comfort, and we take pride in delivering virtually pain-free surgical interventions. <Link to="/services#surgical" className="text-teal-600 hover:text-teal-700 font-medium">View our surgical services →</Link></p>
                  }
                  isOpen={openItems['m6']} 
                  onClick={() => toggleItem('m6')} 
                />
                <FAQItem 
                  question="Do you offer Termination of Pregnancy?" 
                  answer={
                    <p>This service is not available at any of our practice locations.</p>
                  }
                  isOpen={openItems['m7']} 
                  onClick={() => toggleItem('m7')} 
                />
              </CardContent>
            </Card>
          </div>

          {/* Dental Services */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-600" />
              Dental Services
            </h2>
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <FAQItem 
                  question="What dental services do you offer?" 
                  answer={
                    <p>Our dental department provides comprehensive oral healthcare services, from preventive dentistry and routine maintenance to restorative procedures and cosmetic treatments. Our qualified dental professionals utilise contemporary techniques and equipment to deliver exceptional results. <Link to="/services#dental" className="text-teal-600 hover:text-teal-700 font-medium">Discover our full range of dental services →</Link></p>
                  }
                  isOpen={openItems['d1']} 
                  onClick={() => toggleItem('d1')} 
                />
                <FAQItem 
                  question="How often should I visit the dentist?" 
                  answer={
                    <p>Professional dental associations recommend biannual dental visits – that's every six months – for routine examinations and professional cleaning. These regular check-ups are essential for early detection of potential issues, prevention of gum disease, and maintenance of optimal oral health. Consistent preventive care ultimately reduces the need for more extensive and costly treatments in the future.</p>
                  }
                  isOpen={openItems['d2']} 
                  onClick={() => toggleItem('d2')} 
                />
                <FAQItem 
                  question="Do you offer emergency dental care?" 
                  answer={
                    <p>Yes, we understand that dental emergencies can occur unexpectedly and often cause significant discomfort. Our practice accommodates emergency dental cases during operating hours, providing prompt assessment and treatment to address acute dental conditions. We prioritise pain relief and infection control to ensure patient comfort and prevent complications.</p>
                  }
                  isOpen={openItems['d3']} 
                  onClick={() => toggleItem('d3')} 
                />
                <FAQItem 
                  question="What should I do if I have a toothache?" 
                  answer={
                    <p>Dental pain should never be ignored, as it often indicates an underlying condition requiring professional attention. We recommend visiting our dental clinic promptly for a comprehensive consultation and diagnostic X-ray imaging. Our dental team will conduct a thorough assessment to identify the cause of your discomfort and develop an appropriate treatment plan tailored to your specific needs. Early intervention typically results in simpler, more conservative treatment options.</p>
                  }
                  isOpen={openItems['d4']} 
                  onClick={() => toggleItem('d4')} 
                />
                <FAQItem 
                  question="Do you provide teeth whitening services?" 
                  answer={
                    <p>Indeed, we offer professional teeth whitening services – a cosmetic procedure distinct from routine dental cleaning. Our whitening treatment involves a controlled bleaching process that safely lightens the enamel of your teeth. We create customised clear trays specifically fitted to your dental structure, allowing you to achieve your desired shade gradually and comfortably. Our dental professionals will guide you through the process and recommend the optimal treatment protocol for your aesthetic goals.</p>
                  }
                  isOpen={openItems['d5']} 
                  onClick={() => toggleItem('d5')} 
                />
              </CardContent>
            </Card>
          </div>

          {/* Certificates & Medicals */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-600" />
              Certificates & Medicals
            </h2>
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <FAQItem 
                  question="Can I get a PrDP (Professional Driving Permit) medical done here?" 
                  answer={
                    <p>Absolutely. We are authorised to conduct Professional Driving Permit medical examinations in accordance with Department of Transport requirements. Our thorough assessment ensures compliance with all regulatory standards while providing efficient service. <Link to="/prices" className="text-teal-600 hover:text-teal-700 font-medium">View our PrDP pricing and requirements →</Link></p>
                  }
                  isOpen={openItems['c1']} 
                  onClick={() => toggleItem('c1')} 
                />
                <FAQItem 
                  question="Do you provide fitness certificates for employment?" 
                  answer={
                    <p>Yes, we conduct occupational health assessments and issue fitness certificates for a wide range of employers and industries. Our medical examinations are comprehensive, professionally documented, and compliant with occupational health regulations. We work with numerous corporate clients to ensure their workforce meets the required health and safety standards.</p>
                  }
                  isOpen={openItems['c2']} 
                  onClick={() => toggleItem('c2')} 
                />
                <FAQItem 
                  question="How long does a medical examination take?" 
                  answer={
                    <p>The duration of occupational health assessments varies depending on the specific requirements of the examination. A standard fitness or employment medical typically takes between 15 minutes to one hour, contingent upon the investigations mandated by your employer or regulatory body. <span className="font-semibold">Please note that these examinations require advance booking</span> to ensure adequate time allocation and proper preparation of necessary testing equipment.</p>
                  }
                  isOpen={openItems['c3']} 
                  onClick={() => toggleItem('c3')} 
                />
                <FAQItem 
                  question="What documents do I need to bring for a PrDP medical?" 
                  answer={
                    <p>To facilitate your PrDP medical examination, please ensure you bring the following documentation: your original South African Identity Document (or a certified copy thereof), along with the official PrDP application form obtained from the Department of Transport. Having these documents ready will ensure a smooth and efficient examination process. If you have any previous medical records relevant to the assessment, these may also be helpful.</p>
                  }
                  isOpen={openItems['c4']} 
                  onClick={() => toggleItem('c4')} 
                />
              </CardContent>
            </Card>
          </div>

        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="faq-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl rounded-3xl bg-gradient-to-br from-teal-600 to-teal-700 overflow-hidden">
            <CardContent className="p-8 lg:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Still Have Questions?
              </h2>
              <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
                Our dedicated team is here to assist you. Reach out to us directly for personalised guidance and support.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  asChild
                  className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="faq-call-btn"
                >
                  <a href="tel:0399741570">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us
                  </a>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
                  data-testid="faq-whatsapp-btn"
                >
                  <a href="https://wa.me/27828951916" target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
